/**
 * Validation utilities for form inputs
 */

// Email regex pattern
const EMAIL_REGEX = /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;

// Strong password requires: lowercase, uppercase, digit, special char, 8+ chars
export const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

// Phone number: 10 digits
const PHONE_REGEX = /^\d{10}$/;

export const validators = {
  email: (email: string): { valid: boolean; error?: string } => {
    if (!email) return { valid: false, error: "Email is required" };
    if (!EMAIL_REGEX.test(email)) return { valid: false, error: "Invalid email format" };
    return { valid: true };
  },

  password: (password: string): { valid: boolean; error?: string } => {
    if (!password) return { valid: false, error: "Password is required" };
    if (password.length < 8) return { valid: false, error: "Password must be at least 8 characters" };
    if (!STRONG_PASSWORD_REGEX.test(password)) {
      return {
        valid: false,
        error: "Password must contain uppercase, lowercase, number, and special character",
      };
    }
    return { valid: true };
  },

  passwordMatch: (password: string, confirmPassword: string): { valid: boolean; error?: string } => {
    if (password !== confirmPassword) return { valid: false, error: "Passwords do not match" };
    return { valid: true };
  },

  phone: (phone: string): { valid: boolean; error?: string } => {
    const digitsOnly = phone.replace(/\D/g, "");
    if (!digitsOnly) return { valid: false, error: "Phone number is required" };
    if (digitsOnly.length !== 10) return { valid: false, error: "Phone number must be 10 digits" };
    return { valid: true };
  },

  name: (name: string, maxLength: number = 50): { valid: boolean; error?: string } => {
    if (!name) return { valid: false, error: "Name is required" };
    if (name.length > maxLength) return { valid: false, error: `Maximum ${maxLength} characters allowed` };
    if (!/^[A-Za-z][A-Za-z ]*$/.test(name)) {
      return { valid: false, error: "Name must start with a letter and contain only letters and spaces" };
    }
    return { valid: true };
  },

  verificationCode: (code: string): { valid: boolean; error?: string } => {
    if (!code) return { valid: false, error: "Verification code is required" };
    if (!/^\d{6}$/.test(code)) return { valid: false, error: "Verification code must be 6 digits" };
    return { valid: true };
  },
};

/**
 * Get password strength indicator
 */
export function getPasswordStrength(password: string): {
  strength: "weak" | "fair" | "good" | "strong";
  percentage: number;
} {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[\W_]/.test(password)) score++;

  const strengths: ("weak" | "fair" | "good" | "strong")[] = ["weak", "weak", "fair", "good", "good", "strong"];
  const strength = strengths[Math.min(score, 5)];
  const percentage = (score / 6) * 100;

  return { strength, percentage };
}
